import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  ScanBarcode,
  Search,
  Calendar,
  BarChart3,
  Activity,
  Settings,
  LogOut,
  Menu,
  X,
  Video,
  ChevronLeft
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isManager, isExecutive } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define navigation based on role
  const getNavItems = () => {
    const items = [];

    if (isAdmin) {
      items.push(
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/packing', label: 'Packing Station', icon: ScanBarcode },
        { path: '/mobile-packing', label: 'Mobile Packing Station', icon: ScanBarcode },
        { path: '/search', label: 'Search', icon: Search },
        { path: '/daily', label: 'Daily', icon: Calendar },
        { path: '/reports', label: 'Reports', icon: BarChart3 },
        { path: '/logs', label: 'Activity Logs', icon: Activity },
        { path: '/settings', label: 'Settings', icon: Settings }
      );
    } else if (isManager) {
      items.push(
        { path: '/search', label: 'Search', icon: Search },
        { path: '/daily', label: 'Daily', icon: Calendar },
        { path: '/reports', label: 'Reports', icon: BarChart3 },
        { path: '/logs', label: 'Activity Logs', icon: Activity },
        { path: '/settings', label: 'Settings', icon: Settings }
      );
    } else if (isExecutive) {
      items.push(
        { path: '/packing', label: 'Packing Station', icon: ScanBarcode },
        { path: '/mobile-packing', label: 'Mobile Packing Station', icon: ScanBarcode },
        { path: '/settings', label: 'Settings', icon: Settings }
      );
    }

    return items;
  };

  const navItems = getNavItems();
  const isPackingStation = location.pathname === '/packing';

  // Full screen layout for packing station
  if (isPackingStation) {
    return (
      <div className="h-screen flex flex-col bg-zinc-950">
        {/* Minimal Header */}
        <header className="h-14 bg-zinc-900/80 backdrop-blur border-b border-zinc-800 flex items-center justify-between px-4 z-50">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(isAdmin ? '/dashboard' : '/settings')}
              className="text-zinc-400 hover:text-white"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-zinc-800" />
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-indigo-400" />
              <span className="font-semibold text-white">SND VMS</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-400">
              {user?.name} <span className="text-zinc-600">({user?.role})</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-hidden">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'} bg-zinc-900 border-r border-zinc-800 transition-all duration-300`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600/20 rounded-lg flex items-center justify-center border border-indigo-500/30">
                <Video className="w-4 h-4 text-indigo-400" />
              </div>
              <span className="font-bold text-white tracking-tight">SND VMS</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-zinc-400 hover:text-white"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive
                  ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30'
                  : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                } ${!sidebarOpen ? 'justify-center' : ''}`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-3 border-t border-zinc-800">
          <div className={`flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
            <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-300 font-medium">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-red-400"
              data-testid="sidebar-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-indigo-400" />
          <span className="font-bold text-white">SND VMS</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-zinc-400"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-14 bg-zinc-900 z-40 p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive
                    ? 'bg-indigo-600/20 text-indigo-400'
                    : 'text-zinc-400 hover:bg-zinc-800'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="absolute bottom-4 left-4 right-4">
            <Button onClick={handleLogout} variant="outline" className="w-full border-zinc-700">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:overflow-auto pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
};

export default Layout;
